// Imports contracts — the one import framework (docs/use-cases/imports.md, UC-IMPORTS-001..006; modules-and-api.md §5.21;
// working decisions W6 and W8 of docs/sprints/sprint-04.md; A8).
//
// Sprint 4 foundation: the vocabularies an import job, its working list and its mapping profiles share — in their tables'
// CHECK constraints and on their routes — spelled as the use cases spell them, and the file's limits. The job's status machine
// (`import`, with `Discarded`) is in status-machines.ts. The request and response schemas, the two shipped layouts of
// BR-IMPORTS-006 (Generic CSV and Seiko CSV, constants rather than rows) and the CSV reader are the imports module's: they
// are added with its routes, not by the foundation — the schemas below, at the end of this file.
import { z } from "zod";
import { SUPPLIER_PRICE_LIST_KINDS } from "./pricing";
import { IMPORT_JOB_STATUSES } from "./status-machines";

/**
 * The kinds of import (W8): Sprint 4 builds the first, `PriceList` — a lens lab's price list (BR-IMPORTS-016). Later kinds —
 * medical-aid tariffs (Sprint 5), product bulk import and vendor stock levels, the patient take-on (Sprint 11) — reuse the
 * same routes with their own kind and code. Every route on a job checks the kind's own code beside `imports.job.*`
 * (BR-IMPORTS-001).
 */
export const IMPORT_KINDS = ["PriceList"] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];

/** §5.21: the phase a Failed job failed in — its check, its Apply run, or a run stopped by the organisation's suspension (BR-IMPORTS-022). */
export const IMPORT_FAILED_PHASES = ["check", "import", "suspended"] as const;
export type ImportFailedPhase = (typeof IMPORT_FAILED_PHASES)[number];

/** BR-IMPORTS-015: the supplier's organisation-wide list, or the acting branch's own list, which overrides it line by line there. */
export const IMPORT_SCOPES = ["AllBranches", "Branch"] as const;
export type ImportScope = (typeof IMPORT_SCOPES)[number];

/** BR-IMPORTS-011: "Valid lines only" (the default) or "All or nothing". */
export const IMPORT_COMMIT_MODES = ["ValidOnly", "AllOrNothing"] as const;
export type ImportCommitMode = (typeof IMPORT_COMMIT_MODES)[number];

/** BR-IMPORTS-008: a working-list line's verdict at the check — the file's lines, then the current list's lines it no longer carries. */
export const IMPORT_ROW_VERDICTS = ["New", "Changed", "Unchanged", "Discontinued", "Error"] as const;
export type ImportRowVerdict = (typeof IMPORT_ROW_VERDICTS)[number];

/** BR-IMPORTS-018: what Apply made of a line — the counts created, changed, unchanged, skipped, errored and flagged. */
export const IMPORT_ROW_OUTCOMES = ["Created", "Changed", "Unchanged", "Skipped", "Errored", "Flagged"] as const;
export type ImportRowOutcome = (typeof IMPORT_ROW_OUTCOMES)[number];

/** BR-PRICING-021: where a line's derived selling price comes from — the file's recommended retail price, or the list's markup. */
export const IMPORT_SELL_SOURCES = ["recommended_retail", "markup"] as const;
export type ImportSellSource = (typeof IMPORT_SELL_SOURCES)[number];

/** BR-IMPORTS-002, BR-IMPORTS-005: a CSV's field separator — a comma, a semicolon or a tab — and its decimal separator. */
export const CSV_SEPARATORS = [",", ";", "\t"] as const;
export type CsvSeparator = (typeof CSV_SEPARATORS)[number];
export const DECIMAL_SEPARATORS = [".", ","] as const;
export type DecimalSeparator = (typeof DECIMAL_SEPARATORS)[number];

/**
 * BR-IMPORTS-005: the fields a price-list mapping may fill, each from at most one column — the supplier lens code and the lab
 * price (both required), the description (required on a new line), the recommended retail price, the SAOA lens code, the
 * range and the lens attributes a lens product records, its power ranges among them under the lens product's own field names:
 * sphere, cylinder and add, each a minimum and a maximum in dioptres (BR-CATALOGUE-008; CAT-42, the completeness audit of
 * 23 Oct 2026).
 */
export const PRICE_LIST_MAPPING_FIELDS = [
  "supplierCode", "labPrice", "description", "recommendedRetail", "saoaCode", "range", "index", "design", "material", "diameter",
  "sphereMin", "sphereMax", "cylinderMin", "cylinderMax", "addMin", "addMax",
] as const;
export type PriceListMappingField = (typeof PRICE_LIST_MAPPING_FIELDS)[number];
/** BR-IMPORTS-005: a mapping without these is refused (`columnMap`, `required_field`). */
export const PRICE_LIST_REQUIRED_MAPPING_FIELDS = ["supplierCode", "labPrice"] as const satisfies readonly PriceListMappingField[];

/** The limits the imports use cases state (BR-IMPORTS-002, -005, -006, -007, -023; UC-IMPORTS-001). */
export const IMPORT_LIMITS = {
  /** A CSV of at most 5 MB and 20 000 price lines, its header row within the first 20 lines. */
  fileBytesMax: 5_242_880, priceLinesMax: 20_000, headerRowMax: 20,
  /** A list's name and a profile's name, at most 100 characters. */
  listNameMax: 100, profileNameMax: 100,
  /** A supplier lens code, at most 40 characters — the most a product's supplier code holds (BR-CATALOGUE-011). */
  supplierCodeMax: 40,
} as const;

// --- The routes' request and response schemas (§5.21; UC-IMPORTS-001..006), added with the imports module's routes -------------
// Fields are taken loosely where a use case names the refusal's own code — a missing list date is `required`, a markup outside
// its range `out_of_range`, a header row outside lines 1–20 `out_of_range` — and the server answers those itself.

/** BR-IMPORTS-001: the code a kind's own routes check beside `imports.job.*` — a price list is Owner and Admin (W6). */
export const IMPORT_KIND_PERMISSIONS = { PriceList: "pricing.price_list.write" } as const satisfies Readonly<Record<ImportKind, string>>;

/** BR-IMPORTS-002: a file of 5 MB travels as at most this much standard base64. */
export const IMPORT_FILE_BASE64_MAX = Math.ceil(IMPORT_LIMITS.fileBytesMax / 3) * 4;

/** BR-CATALOGUE-008's five lens designs as an import reads a design (the catalogue's `LENS_DESIGN_TYPES`, mirrored by value). */
export const IMPORT_LENS_DESIGNS = ["SingleVision", "Bifocal", "Trifocal", "Progressive", "Occupational"] as const;
export type ImportLensDesign = (typeof IMPORT_LENS_DESIGNS)[number];

/** The error report's header (UC-IMPORTS-005 A2; BR-GOVERNANCE-015). */
export const IMPORT_ERROR_REPORT_HEADER = ["row", "code", "field", "error", "message"] as const;

/** What may be done with an import in its status (UC-IMPORTS-005 step 3, A3). */
export const IMPORT_MOVES = ["edit", "map", "check", "select", "commit", "resume", "discard"] as const;
export type ImportMove = (typeof IMPORT_MOVES)[number];

/** The shipped layouts' ids — read-only, the same for every organisation (BR-IMPORTS-006). */
export const SHIPPED_IMPORT_LAYOUT_IDS = { genericCsv: "shipped-generic-csv", seikoCsv: "shipped-seiko-csv" } as const;

const Limit = z.coerce.number().int().min(1).max(200).default(50);
const Flag = z.enum(["true", "false"]);
const OpaqueId = z.string().min(1).max(64);
const Field = z.enum(PRICE_LIST_MAPPING_FIELDS);
/** A value of the enum, or several of them comma-separated or repeated. */
const oneOrMore = <T extends readonly [string, ...string[]]>(values: T) => z.union([z.string(), z.array(z.string())])
  .transform((v) => (Array.isArray(v) ? v : v.split(",")).map((s) => s.trim()).filter((s) => s.length > 0))
  .pipe(z.array(z.enum(values)).min(1));

/** The file carried inline (BR-IMPORTS-002, -003): its name and its bytes as standard base64. A declared type is never trusted. */
export const ImportFileUploadSchema = z.strictObject({
  fileName: z.string().trim().min(1).max(255),
  data: z.string(),
  contentType: z.string().max(200).optional(),
});

/** UC-IMPORTS-001: the upload — the kind, the supplier, the scope and the list's terms, with the file. */
export const ImportJobCreateSchema = z.strictObject({
  kind: z.enum(IMPORT_KINDS),
  supplierId: OpaqueId,
  scope: z.enum(IMPORT_SCOPES).optional(),
  /** The supplier's date for the list, `YYYY-MM-DD` (BR-IMPORTS-014): required, checked by the server. */
  listDate: z.string().max(32).nullable().optional(),
  listName: z.string().trim().max(IMPORT_LIMITS.listNameMax).nullable().optional(),
  lensMarkupBp: z.number().int().optional(),
  discountBp: z.number().int().nullable().optional(),
  file: ImportFileUploadSchema,
});
export type ImportJobCreate = z.infer<typeof ImportJobCreateSchema>;

/** UC-IMPORTS-001 A7: the list's name, date, lens markup, discount or scope changed before Apply. */
export const ImportJobPatchSchema = z.strictObject({
  listName: z.string().trim().max(IMPORT_LIMITS.listNameMax).nullable().optional(),
  listDate: z.string().max(32).optional(),
  lensMarkupBp: z.number().int().optional(),
  discountBp: z.number().int().nullable().optional(),
  scope: z.enum(IMPORT_SCOPES).optional(),
});
export type ImportJobPatch = z.infer<typeof ImportJobPatchSchema>;

/**
 * BR-IMPORTS-005: header name → the field it fills. A column naming two fields (`["supplierCode", "saoaCode"]`) is refused
 * `duplicate_column`, two columns naming one field `duplicate_field`; a blank header cell is named by its position, "Column 6".
 */
export const ImportColumnMapSchema = z.record(z.string().max(200), z.union([Field, z.array(Field).min(1).max(PRICE_LIST_MAPPING_FIELDS.length)]));
export type ImportColumnMap = z.infer<typeof ImportColumnMapSchema>;

/** UC-IMPORTS-002: the mapping an import is checked with, and the layout or profile it came from. */
export const ImportMappingInputSchema = z.strictObject({
  columnMap: ImportColumnMapSchema,
  /** 1–20 (BR-IMPORTS-002); line 1 when not sent. */
  headerRow: z.number().int().optional(),
  separator: z.enum(CSV_SEPARATORS).optional(),
  decimalSeparator: z.enum(DECIMAL_SEPARATORS).optional(),
  pricesIncludeVat: z.boolean().optional(),
  recommendedRetailIncludesVat: z.boolean().optional(),
  /** The shipped layout's or the profile's name the mapping was filled from, for the audit entry. */
  layout: z.string().max(200).nullable().optional(),
  /** The saved profile the mapping came from: its use is recorded on it (BR-IMPORTS-006). */
  profileId: OpaqueId.nullable().optional(),
});
export type ImportMappingInput = z.infer<typeof ImportMappingInputSchema>;

/** BR-IMPORTS-009: tick or untick lines — named rows, or every line a filter selects, on every page. */
export const ImportSelectionSchema = z.strictObject({
  ticked: z.boolean(),
  rows: z.array(OpaqueId).min(1).max(1_000).optional(),
  filter: z.strictObject({
    verdict: z.enum(IMPORT_ROW_VERDICTS).optional(),
    design: z.string().trim().min(1).max(60).optional(),
    range: z.string().trim().min(1).max(120).optional(),
  }).optional(),
}).superRefine((s, ctx) => {
  if ((s.rows === undefined) === (s.filter === undefined)) ctx.addIssue({ code: "custom", path: ["rows"], message: "name the rows or a filter, one of them", params: { code: "required" } });
});
export type ImportSelection = z.infer<typeof ImportSelectionSchema>;

/** UC-IMPORTS-004: Apply — valid lines only by default (BR-IMPORTS-011); a price set by hand kept unless asked (BR-PRICING-022). */
export const ImportCommitSchema = z.strictObject({
  mode: z.enum(IMPORT_COMMIT_MODES).optional(),
  recalculateSellingPrices: z.boolean().optional(),
});
export type ImportCommit = z.infer<typeof ImportCommitSchema>;

/** UC-IMPORTS-002 A2: a profile saved for one supplier and one kind. */
export const ImportProfileCreateSchema = z.strictObject({
  supplierId: OpaqueId,
  kind: z.enum(IMPORT_KINDS),
  name: z.string().trim().max(IMPORT_LIMITS.profileNameMax),
  separator: z.enum(CSV_SEPARATORS).optional(),
  decimalSeparator: z.enum(DECIMAL_SEPARATORS).optional(),
  headerRow: z.number().int().optional(),
  pricesIncludeVat: z.boolean().optional(),
  recommendedRetailIncludesVat: z.boolean().optional(),
  columnMap: ImportColumnMapSchema,
});
export type ImportProfileCreate = z.infer<typeof ImportProfileCreateSchema>;

/** UC-IMPORTS-002 A4: a profile renamed, or its mapping changed. */
export const ImportProfilePatchSchema = z.strictObject({
  name: z.string().trim().max(IMPORT_LIMITS.profileNameMax).optional(),
  separator: z.enum(CSV_SEPARATORS).optional(),
  decimalSeparator: z.enum(DECIMAL_SEPARATORS).optional(),
  headerRow: z.number().int().optional(),
  pricesIncludeVat: z.boolean().optional(),
  recommendedRetailIncludesVat: z.boolean().optional(),
  columnMap: ImportColumnMapSchema.optional(),
});
export type ImportProfilePatch = z.infer<typeof ImportProfilePatchSchema>;

/** UC-IMPORTS-005: the history — newest first, a page at a time; only the kinds whose code the caller holds. */
export const ImportJobListQuerySchema = z.object({
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
  supplierId: OpaqueId.optional(), status: oneOrMore(IMPORT_JOB_STATUSES).optional(), kind: z.enum(IMPORT_KINDS).optional(),
});
export type ImportJobListQuery = z.infer<typeof ImportJobListQuerySchema>;

/** UC-IMPORTS-003: the working list in file order, then the discontinued lines; `view=review` the lines to review. */
export const ImportRowsQuerySchema = z.object({
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
  verdict: oneOrMore(IMPORT_ROW_VERDICTS).optional(), view: z.enum(["review", "all"]).optional(),
  design: z.string().trim().min(1).max(60).optional(), range: z.string().trim().min(1).max(120).optional(), ticked: Flag.optional(),
});
export type ImportRowsQuery = z.infer<typeof ImportRowsQuerySchema>;

/** BR-IMPORTS-006: the supplier's live profiles and the shipped layouts, for the kinds the caller may import. */
export const ImportProfileListQuerySchema = z.object({
  limit: Limit, cursor: z.string().max(512).optional(), supplierId: OpaqueId.optional(), kind: z.enum(IMPORT_KINDS).optional(),
});
export type ImportProfileListQuery = z.infer<typeof ImportProfileListQuerySchema>;

const UserRef = z.object({ id: z.string(), controlId: z.number().int(), displayName: z.string() });
const BranchRef = z.object({ id: z.string(), code: z.string(), name: z.string() });
const Count = z.number().int().min(0);
const CentsOrNull = z.number().int().nullable();

export const ImportLayoutRefSchema = z.object({ name: z.string(), shipped: z.boolean(), profileId: z.string().nullable() });
export const ImportMappingViewSchema = z.object({
  columnMap: z.record(z.string(), Field), headerRow: z.number().int(), separator: z.enum(CSV_SEPARATORS), decimalSeparator: z.enum(DECIMAL_SEPARATORS),
  pricesIncludeVat: z.boolean(), recommendedRetailIncludesVat: z.boolean(),
});
export type ImportMappingView = z.infer<typeof ImportMappingViewSchema>;
/** BR-IMPORTS-018: the check's counts; `ticked` is the lines ticked now. */
export const ImportCheckCountsSchema = z.object({
  parsed: Count, new: Count, changed: Count, unchanged: Count, errors: Count, duplicateCodes: Count, discontinued: Count, toReview: Count, ticked: Count,
});
export type ImportCheckCounts = z.infer<typeof ImportCheckCountsSchema>;
/**
 * BR-IMPORTS-018: what Apply made of the lines — `flagged` the discontinued lines flagged "Not in latest price list" — and of
 * their lenses: `hidden` the lenses its final write hid from new orders and `broughtBack` those a list had hid that it offered
 * again (BR-IMPORTS-010, BR-CATALOGUE-020; DVN's answer of 24 Oct 2026, the board's question 4), each null for an import whose
 * Apply recorded neither — one applied before the two were counted.
 */
export const ImportOutcomeCountsSchema = z.object({
  created: Count, changed: Count, unchanged: Count, skipped: Count, errored: Count, flagged: Count, hidden: Count.nullable(), broughtBack: Count.nullable(),
});
export type ImportOutcomeCounts = z.infer<typeof ImportOutcomeCountsSchema>;
/** BR-IMPORTS-012: lines done of lines to do, while a run is under way. */
export const ImportProgressSchema = z.object({ phase: z.enum(["Check", "Import"]), done: Count, total: Count });
export const ImportFailureSchema = z.object({ phase: z.enum(IMPORT_FAILED_PHASES), reason: z.string() });
/** BR-IMPORTS-019: the file was imported for the supplier before. */
export const ImportWarningSchema = z.object({ code: z.literal("same_file"), importId: z.string(), uploadedAt: z.string(), uploadedBy: UserRef.nullable() });
export type ImportWarning = z.infer<typeof ImportWarningSchema>;

/** UC-IMPORTS-005 step 3: one import — its terms, its own mapping, its counts, its progress or failure and what may be done. */
export const ImportJobViewSchema = z.object({
  id: z.string(),
  kind: z.enum(IMPORT_KINDS),
  status: z.enum(IMPORT_JOB_STATUSES),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable(),
  scope: z.enum(IMPORT_SCOPES),
  branch: BranchRef.nullable(),
  listName: z.string(),
  listDate: z.string().nullable(),
  lensMarkupBp: z.number().int().nullable(),
  discountBp: z.number().int().nullable(),
  file: z.object({ name: z.string(), sizeBytes: Count, sha256: z.string() }),
  /** Price lines below the header row (BR-IMPORTS-002). */
  lines: Count,
  header: z.array(z.string()),
  /** The first three price lines' cells. */
  sample: z.array(z.array(z.string())),
  /**
   * UC-IMPORTS-002 A1 (TC-IMPORTS-074): the separator and header row the header and samples were read at — the mapping's, or for
   * a file no layout recognised the separator its first line holds most of and header row 1 — so a reopened import maps without
   * its file. Null for an import stored before it with no mapping.
   */
  previewAt: z.object({ separator: z.enum(CSV_SEPARATORS), headerRow: z.number().int() }).nullable(),
  layout: ImportLayoutRefSchema.nullable(),
  mapping: ImportMappingViewSchema.nullable(),
  /**
   * The supplier's current list for the scope that the new list follows — of its own kind, else the newest of those it
   * supersedes — whose terms it takes by default (BR-PRICING-019, BR-IMPORTS-023); null for the scope's first list.
   */
  currentList: z.object({ id: z.string(), name: z.string(), listDate: z.string(), lineCount: Count }).nullable(),
  /**
   * Every current list of the supplier at the scope, whatever its kind, newest first: what the check compares with and Apply
   * supersedes (BR-PRICING-019, BR-IMPORTS-008; DVN's answer of 24 Oct 2026, the board's question 18). Empty for a first list.
   */
  supersedes: z.array(z.object({ id: z.string(), name: z.string(), kind: z.enum(SUPPLIER_PRICE_LIST_KINDS), listDate: z.string(), lineCount: Count })),
  /** The list Apply wrote: a Draft until the final write, then Current; withdrawn when a failed run is discarded. */
  priceListId: z.string().nullable(),
  counts: ImportCheckCountsSchema.nullable(),
  outcome: ImportOutcomeCountsSchema.nullable(),
  progress: ImportProgressSchema.nullable(),
  failure: ImportFailureSchema.nullable(),
  commit: z.object({ mode: z.enum(IMPORT_COMMIT_MODES), recalculateSellingPrices: z.boolean() }).nullable(),
  uploadedAt: z.string(),
  uploadedBy: UserRef.nullable(),
  uploadBranch: BranchRef.nullable(),
  checkedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  appliedBy: UserRef.nullable(),
  completedAt: z.string().nullable(),
  discardedAt: z.string().nullable(),
  discardedBy: UserRef.nullable(),
  moves: z.array(z.enum(IMPORT_MOVES)),
});
export type ImportJobView = z.infer<typeof ImportJobViewSchema>;
export const ImportJobWriteResultSchema = ImportJobViewSchema.extend({ warnings: z.array(ImportWarningSchema) });
export type ImportJobWriteResult = z.infer<typeof ImportJobWriteResultSchema>;
export const ImportJobPageSchema = z.object({ items: z.array(ImportJobViewSchema), nextCursor: z.string().optional(), totalCount: z.number().int().optional() });

/** A line's error (BR-IMPORTS-007): the field, the reason's code and words, and for a duplicate code the other rows. */
export const ImportRowErrorSchema = z.object({ field: z.string(), code: z.string(), message: z.string(), rows: z.array(z.number().int()).optional() });
/** A lens's power ranges in dioptres — sphere, cylinder and add, each from and to — null where an end is none (BR-CATALOGUE-008). */
export const ImportLensRangesSchema = z.object({
  sphereMin: z.number().nullable(), sphereMax: z.number().nullable(), cylinderMin: z.number().nullable(), cylinderMax: z.number().nullable(),
  addMin: z.number().nullable(), addMax: z.number().nullable(),
});
export type ImportLensRanges = z.infer<typeof ImportLensRangesSchema>;
export type ImportRowError = z.infer<typeof ImportRowErrorSchema>;

/** UC-IMPORTS-003 step 5, UC-IMPORTS-005 step 3: a line of the working list with its old and new prices and what it became. */
export const ImportRowViewSchema = z.object({
  id: z.string(),
  rowNo: z.number().int().nullable(),
  verdict: z.enum(IMPORT_ROW_VERDICTS),
  ticked: z.boolean(),
  selectable: z.boolean(),
  outcome: z.enum(IMPORT_ROW_OUTCOMES).nullable(),
  code: z.string().nullable(),
  matchedBy: z.enum(["code", "saoa_code"]).nullable(),
  description: z.string().nullable(),
  /** The refractive index, 1.400–2.000. */
  index: z.number().nullable(),
  design: z.enum(IMPORT_LENS_DESIGNS).nullable(),
  range: z.string().nullable(),
  /** The lab cost ex VAT, net of the list's discount: the current line's, and the file's. */
  labCostCents: z.object({ old: CentsOrNull, new: CentsOrNull }),
  /**
   * The selling price incl VAT: in force when the line was checked, and as pricing derives it from the line (BR-IMPORTS-010). On
   * an applied import a ticked changed or new line with a product reads what the final write did (UC-IMPORTS-004 step 10, A21;
   * UC-IMPORTS-005 step 3): a price it set, the price it replaced and the price it set, from the import's own history entry —
   * the price replaced, at a branch with no price of its own, the tenant price the branch charged; a price it did not set, the
   * price in force when the import completed, in both. Every other line of an applied import reads as checked.
   */
  sellCents: z.object({ old: CentsOrNull, new: CentsOrNull }),
  /**
   * The line's price is kept as a person set it (BR-PRICING-022), not the list's. At the check: a price a person set, which the
   * list's would replace only with "Recalculate selling prices from markup" — `sellCents` that price, twice. On an applied import:
   * a ticked changed or new line whose price the final write did not set, the price in force when the import completed not being
   * the list's — a person having set it at the check, between the check and Apply, or while the run waited before its final
   * write (UC-IMPORTS-004 A21) — `sellCents` that price, twice. Either way `recalculatedSellCents` is the list's price. Null on a
   * line whose price the import set or the list's price stands.
   */
  sellKept: z.enum(["set_by_hand"]).nullable(),
  /**
   * On a line whose price is kept as set by hand, the price the list derives for it: on a changed or new line, what "Recalculate
   * selling prices from markup" gives where it is above the kept price (`sellCents.new`); below it, the price the line would fall
   * to, which Recalculate never applies to a price a person set (BR-PRICING-022). On an unchanged line, the list's price, which
   * Apply never sets, Recalculate or not (BR-IMPORTS-010; UC-IMPORTS-003 A8 step 4). Null on every other line.
   */
  recalculatedSellCents: CentsOrNull,
  /**
   * What the line's derived selling price is (BR-PRICING-021): the file's recommended retail price, as given and never rounded
   * (`recommended_retail`), or the lab price plus the list's lens markup, rounded by its rule (`markup`) — the new price, or on a
   * line whose price is kept as set by hand the price the list derives for it (`recalculatedSellCents`). Null on a discontinued
   * line or a line in error, which derive none.
   */
  sellFrom: z.enum(IMPORT_SELL_SOURCES).nullable(),
  /**
   * On a line of an organisation-wide import whose power ranges are not its lens's own (UC-IMPORTS-003 A17; DVN's answer of
   * 28 Oct 2026 to the Sprint 4 board's question 37, change 10): the lens's ranges when the line was checked (`old`) and as Apply
   * leaves them (`new`) — each end the line gives replacing the lens's, an end it leaves blank kept unless it would lie past the
   * end the line gives, when it is cleared (BR-IMPORTS-010, BR-CATALOGUE-008). Read as checked whatever the import's status: Apply
   * writes them only from a ticked changed or new line. Null on every other line — one whose ranges are its lens's, or that gives
   * none, a branch's own list's line, a discontinued line and a line in error.
   */
  lensRanges: z.object({ old: ImportLensRangesSchema, new: ImportLensRangesSchema }).nullable(),
  errors: z.array(ImportRowErrorSchema),
  product: z.object({
    id: z.string(), sku: z.string(), name: z.string(), status: z.enum(["Active", "Inactive"]),
    /**
     * The supplier's list whose final write hid the lens from new orders, and the import that applied it (BR-CATALOGUE-020), as
     * the product's own read names it; null for any other lens — one a person deactivated among them. A lens a list hid comes
     * back when the new list carries its code; one a person deactivated never does (UC-IMPORTS-003 A16).
     */
    hiddenByPriceList: z.object({ priceListId: z.string(), name: z.string(), listDate: z.string(), importJobId: z.string() }).nullable(),
    /**
     * A lens no applied import has activated — Inactive, live, created by an import that is not Imported: one whose run stopped,
     * or one discarded keeping it (UC-IMPORTS-006 A4) — which this import's final write makes its own and Active when the line is
     * on its list (BR-IMPORTS-010, BR-CATALOGUE-019; UC-IMPORTS-003 A16 step 2). Marked on the lines of an import still to be
     * applied only: false on every line of one applied or discarded, and for any other lens — one a person deactivated, one a
     * list hid.
     */
    activatedOnApply: z.boolean(),
  }).nullable(),
  /**
   * What keeps a discontinued line's code or lens from Apply's flag and hide (BR-IMPORTS-010, BR-IMPORTS-015, BR-CATALOGUE-020):
   * `carried` — a list of the supplier carries the code, so the line falls back to it, neither flagged nor its lens hidden;
   * `named` — a list of any supplier names the lens, so the line is flagged and the lens stays offered.
   * - Before Apply, and until the import is Imported: as the lists stand when the line is read — the current lists in force but
   *   those its Apply supersedes — the newest such list, a carrying one first (UC-IMPORTS-003 step 6, A15); on an import
   *   discarded, every current list in force.
   * - On an Imported import: as the lists stood when it completed, never as later lists stand (UC-IMPORTS-004 step 10) — the lists
   *   then in force but its own, a list superseded since among them, each list's end as its recorded hand ends had left it then;
   *   `carried` only for a line the import did not flag and `named` only for a flagged one, as its flag, the final write's own
   *   record, says — so possibly a list superseded since, sharing the current list's name.
   * The list's date (`listDate`, the supplier's date for it) and whether it is still Current as the line is read (`current`:
   * false once a later list of its scope has superseded it; a branch's own list ended by hand stays Current, its end on the
   * lists) say which list it was. Null on every other line, and on a discontinued line nothing keeps.
   */
  keptBy: z.object({ reason: z.enum(["carried", "named"]), priceListId: z.string(), name: z.string(), listDate: z.string(), current: z.boolean(), branch: BranchRef.nullable() }).nullable(),
  line: z.object({ id: z.string(), priceListId: z.string() }).nullable(),
});
export type ImportRowView = z.infer<typeof ImportRowViewSchema>;
export const ImportRowPageSchema = z.object({ items: z.array(ImportRowViewSchema), nextCursor: z.string().optional(), totalCount: z.number().int().optional() });

/** BR-IMPORTS-006: a saved profile, or a shipped layout (read-only, no supplier). */
export const ImportProfileViewSchema = z.object({
  id: z.string(),
  kind: z.enum(IMPORT_KINDS),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable(),
  name: z.string(),
  shipped: z.boolean(),
  separator: z.enum(CSV_SEPARATORS),
  decimalSeparator: z.enum(DECIMAL_SEPARATORS),
  headerRow: z.number().int(),
  pricesIncludeVat: z.boolean(),
  recommendedRetailIncludesVat: z.boolean(),
  columnMap: z.record(z.string(), Field),
  lastUsedAt: z.string().nullable(),
  usageCount: Count,
});
export type ImportProfileView = z.infer<typeof ImportProfileViewSchema>;
export const ImportProfilePageSchema = z.object({ items: z.array(ImportProfileViewSchema), nextCursor: z.string().optional() });
